import React, { useState } from 'react';
import maplibregl from 'maplibre-gl';
import { useToast } from '@/hooks/use-toast';

interface SnapshotButtonProps {
  mapRef: React.RefObject<maplibregl.Map>;
  meta: {
    releaseId: string | number;
    selectedLocation?: any;
    center: [number, number];
    zoom: number;
  };
  onSaved: (snapshotId: string) => void;
}

const SnapshotButton: React.FC<SnapshotButtonProps> = ({
  mapRef,
  meta,
  onSaved
}) => {
  const [isCapturing, setIsCapturing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  const handleSnapshot = async () => {
    if (!mapRef.current) {
      toast({
        title: 'Map not available',
        description: 'Please wait for the map to load completely.',
        variant: 'destructive',
      });
      return;
    }

    // Check if map is fully loaded
    if (!mapRef.current.isStyleLoaded()) {
      toast({
        title: 'Map still loading',
        description: 'Please wait a moment and try again.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsCapturing(true);
      
      // Get current map state
      const center = mapRef.current.getCenter();
      const zoom = mapRef.current.getZoom();
      
      // Capture canvas as data URL
      const canvas = mapRef.current.getCanvas();
      const dataUrl = canvas.toDataURL('image/png');
      
      setIsCapturing(false);
      setIsSaving(true);
      
      // Save snapshot
      const formData = new FormData();
      formData.append('dataUrl', dataUrl);
      formData.append('selectedLocation', JSON.stringify(meta.selectedLocation));
      formData.append('releaseId', meta.releaseId.toString());
      formData.append('center', JSON.stringify([center.lng, center.lat]));
      formData.append('zoom', zoom.toString());
      formData.append('meta', JSON.stringify({
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        }
      }));
      
      const response = await fetch('/api/snapshots', {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const result = await response.json();
        onSaved(result.id);
        toast({
          title: 'Snapshot saved',
          description: 'Snapshot saved successfully!',
        });
      } else {
        throw new Error('Failed to save snapshot');
      }

    } catch (error) {
      console.error('Snapshot error:', error);
      toast({
        title: 'Snapshot failed',
        description: 'Failed to capture snapshot',
        variant: 'destructive',
      });
    } finally {
      setIsCapturing(false);
      setIsSaving(false);
    }
  };

  return (
    <button
      onClick={handleSnapshot}
      disabled={isCapturing || isSaving}
      className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white px-4 py-2 rounded text-sm flex items-center space-x-2"
    >
      {isCapturing ? (
        <>
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          <span>Capturing...</span>
        </>
      ) : isSaving ? (
        <>
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          <span>Saving...</span>
        </>
      ) : (
        <>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span>Snapshot</span>
        </>
      )}
    </button>
  );
};

export default SnapshotButton;
